import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { NoteViewModel } from '../../../../../note/models/views/note-view.model';
import { BaseComponent } from '../../../../../shared/base-components/base.component';
import { AutoCompleteComponent } from '../../../../../shared/components/autocomplete/autocomplete.component';
import { ValidationStateModel } from '../../../../../shared/models/validation-results/validation-state.model';
import { ValidationModel } from '../../../../../shared/models/validation-results/validation.model';
import { ControlValidationMessagesService } from '../../../../../shared/validators/control-validation-messages.service';
import { BodyStyleType, IBodyStyleType } from '../../../../models/enums/body-style-type.enum';
import { FuelType, IFuelType } from '../../../../models/enums/fuel-type.enum';
import { GrossVehicleWeightRatingType, IGrossVehicleWeightRatingType } from '../../../../models/enums/gross-vehicle-weight-rating-type.enum';
import { IOdometerReadingType, OdometerReadingType } from '../../../../models/enums/odometer-reading-type.enum';
import { IOdometerReadingUnitType, OdometerReadingUnitType } from '../../../../models/enums/odometer-reading-unit-type.enum';
import { ITonnageRatingDisplayType, TonnageRatingDisplayType } from '../../../../models/enums/tonnage-rating-display-type.enum';
import { ITonnageRatingType, TonnageRatingType } from '../../../../models/enums/tonnage-rating-type.enum';
import { IVINType, VINType } from '../../../../models/enums/v-i-n-type.enum';
import { VehicleColorType } from '../../../../models/enums/vehicle-color-type.enum';
import { IVehicleType, VehicleType } from '../../../../models/enums/vehicle-type.enum';
import { VehicleModel } from '../../../../models/vehicle.model';
import { VehicleService } from '../../../../services/vehicle.service';
import { VehicleMakeModelComponent } from '../../../../shared/vehicle-make-model/vehicle-make-model.component';
import { VehicleAddService } from './vehicle-add.service';

@Component({
    selector: 'vehicle-add',
    templateUrl: './vehicle-add.html'
})
export class VehicleAddComponent extends BaseComponent implements OnInit {
    @ViewChild('makeAutocomplete', { static: true })
    public _makeAutoComplete: AutoCompleteComponent;
    @ViewChild('modelAutocomplete', { static: true })
    public _modelAutoComplete: AutoCompleteComponent;
    @ViewChild('vehicleMakeAdd', { static: true })
    public _vehicleMakeAdd: VehicleMakeModelComponent;
    @ViewChild('vehicleModelAdd', { static: true })
    public _vehicleModelAdd: VehicleMakeModelComponent;

    @Input()
    public canAddNotes = true;
    @Input()
    public canEditVin = true;
    @Input()
    public canModifyFlip: boolean;
    @Input()
    public canModifyMOFR = false;
    @Input()
    public displayBrands = false;
    @Input()
    public searchString: string;
    @Input()
    public vehicle: VehicleModel;
    @Input()
    public vehicleNotes: NoteViewModel[] = [];
    @Input()
    public skipFlpValidation = false;
    @Input()
    public updateVehicle = false;
    @Input()
    public skipCabChasieValidation = false;
    @Output()
    public onValidationErrors = new EventEmitter<ValidationModel>();

    public addVehicleForm: UntypedFormGroup;
    public validationModel: ValidationModel = new ValidationModel([]);
    public vehicleSaved = false;
    public fuelTypes: IFuelType[];
    public activeFuelTypes: IFuelType[];
    public grossVehicleWeightRatingTypes: IGrossVehicleWeightRatingType[];
    public colorTypes: any;
    public odometerReadingTypes: IOdometerReadingType[];
    public odometerReadingUnitTypes: IOdometerReadingUnitType[];
    public vinTypes: IVINType[];
    public vinType: VINType = new VINType();
    public makes: any;
    public tonnageRatingTypes: ITonnageRatingType[];
    public vehicleTypes: IVehicleType[];
    public vehicleType: VehicleType = new VehicleType();
    public bodyStyleTypes: IBodyStyleType[];
    public mouseover = false;
    public vehicles: VehicleModel[];
    public vehicleTypeId: number;
    public vehicleTypeBodyStyleTypeID: number;
    // public newVehicleBrands: any[];
    // public existingBrands: any[];
    public dataLoaded = false;
    public tonnageRatingDisplayTypes: ITonnageRatingDisplayType[];
    public hasBeenSaved = false;
    public blockUserInput = true;
    public forcedModel: string;
    public forcedModelId: number;

    constructor(
        public fb: UntypedFormBuilder,
        public vehicleService: VehicleService,
        private vehicleAddService: VehicleAddService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.setupBrands();
        this.setupTypes();
        this.setupLookupValues();
        this.setDefaultValuesForOmniSearch();
        this.setFormControls();
        this.setTonnageRating();
        this.addVehicleForm.get('vehicleNumber').disable();

        if (this.updateVehicle) {
            this.canEditVin = this.updateVehicle;
        }
    }

    public ngAfterViewInit(): void {
        this.onVINChanged();
        this.setupMakeAutoCompleteHandler();
        this.setupModelAutoCompleteHandler();
        this.setupVehicleMakeAddHandler();
        this.setupVehicleModelAddHandler();
        this.setupMakeAutoCompleteInitCompleteHandler();
    }

    public onVehicleSelect = (vehicleIndex: number): void => {
        let selectedVehicle = this.vehicles[vehicleIndex];
        selectedVehicle.selectedVinPatternId = selectedVehicle.vinPatternId;
        this.setVehicle(selectedVehicle);
    };

    public isFlipReadOnly = (): boolean => {
        if (this.canModifyFlip) {
            return false;
        }

        if (+this.vehicle.originalFactoryListPrice === 0) {
            return false;
        }

        if (this.vehicle.originalFactoryListPrice == null) {
            return false;
        }

        return true;
    };

    public onVINChanged(): void {
        // don't make a call in case of state assigned vehicle. Bug 40399
        if (!this.vehicle || +this.addVehicleForm.get('vinTypeID').value == new VINType().StateAssigned.id) {
            return;
        }

        let errors = this.validateInput();

        if (errors && errors.length > 0) {
            this.onValidationErrors.emit(this.validationModel);
        } else if (this.vehicle.id > 0) {
            this.showTonnageRating();
        } else {
            this.getNewVehiclesFromVin();
        }
    }

    public setVehicle = (vehicleModel): void => {
        this.vehicle = vehicleModel;
        this.vehicles = null;
        this.setModelLookupValues(this.vehicle);
        this.handleValidationResult(this.vehicle);
        let tonnageRatingTypeId = this.addVehicleForm.get('tonnageRatingTypeID').value;

        if (tonnageRatingTypeId) {
            this.vehicleService.getTonnageDisplayValue(tonnageRatingTypeId)
                .subscribe(
                    r => this.addVehicleForm.get('tonnageRatingDisplayTypeId').setValue(r),
                    err => console.log(err));
        }
    };

    public setFormControls = (): void => {
        this.addVehicleForm = this.fb.group({
            vin: [this.searchString || this.vehicle.vin, { updateOn: 'blur' }],
            originalVin: [this.searchString || this.vehicle.vin],
            vinTypeID: [null, Validators.required],
            vehicleNumber: [''],
            modelYear: ['', ControlValidationMessagesService.requiredModelYear],
            fuelTypeID: [null, Validators.required],
            factoryListPrice: ['', ControlValidationMessagesService.nonNegativeIntegerRequired],
            tonnageRatingTypeID: [this.vehicle.tonnageRatingTypeID],
            tonnageRatingDisplayTypeId: [null],
            unladenWeight: ['', [ControlValidationMessagesService.integer, ControlValidationMessagesService.positiveNumber]],
            axles: ['', [ControlValidationMessagesService.integer, ControlValidationMessagesService.positiveNumber]],
            vehicleTypeID: [null, Validators.required],
            bodyStyleTypeID: [null, Validators.required],
            primaryColorTypeID: [null],
            secondaryColorTypeID: [null],
            monthFirstRegistered: ['', [ControlValidationMessagesService.integer, ControlValidationMessagesService.positiveNumber]],
            yearFirstRegistered: ['', [ControlValidationMessagesService.integer, ControlValidationMessagesService.positiveNumber]],
            combinedAxles: ['', [ControlValidationMessagesService.integer, ControlValidationMessagesService.positiveNumber]],
            seats: ['', [ControlValidationMessagesService.integer, ControlValidationMessagesService.positiveNumber]]
        });

        this.addVehicleForm.get('vehicleTypeID').valueChanges
            .pipe(takeUntil(this.componentDestroyed))
            .subscribe(v => this.onVehicleTypeSelected(v));
        this.addVehicleForm.get('bodyStyleTypeID').valueChanges
            .pipe(takeUntil(this.componentDestroyed))
            .subscribe(v => this.onBodyStyleTypeSelected(v));
        this.addVehicleForm.get('vin').valueChanges
            .pipe(takeUntil(this.componentDestroyed))
            .subscribe(v => this.onVINChanged());

        if (this.canEditVin) {
            this.addVehicleForm.get('vin').setValidators(ControlValidationMessagesService.requiredVinPatternCompareToOriginalVin);
        }

        if (this.isLengthWidthRequired()) {
            this.addLengthWidthSeatsFields();
        }

        if (this.updateVehicle) {
            this.addVehicleForm.get('vinTypeID').disable();
            this.addVehicleForm.get('vehicleNumber').disable();
            this.addVehicleForm.get('vehicleTypeID').disable();
            this.addVehicleForm.get('primaryColorTypeID').disable();
            this.addVehicleForm.get('secondaryColorTypeID').disable();
            this.addVehicleForm.get('bodyStyleTypeID').disable();
            this.addVehicleForm.get('fuelTypeID').disable();
            this.addVehicleForm.get('factoryListPrice').disable();
            this.addVehicleForm.get('tonnageRatingTypeID').disable();
            this.addVehicleForm.get('unladenWeight').disable();
            this.addVehicleForm.get('axles').disable();
            // this.addVehicleForm.get('combinedAxles').disable();
            // this.addVehicleForm.get('seats').disable();

            if (this.isLengthWidthRequired()) {
                this.addVehicleForm.get('trailerLength').disable();
                this.addVehicleForm.get('trailerWidth').disable();
            }
        }
    };

    public isValid = (): boolean => {
        var result = this.addVehicleForm.valid;

        if (!result) {
            this.markAsTouched();
        }

        return result;
    };

    public isLengthWidthRequired = (): boolean => (this.addVehicleForm.get('vehicleTypeID').value == this.vehicleType.TentTrailer.id) ||
        (this.addVehicleForm.get('vehicleTypeID').value == this.vehicleType.TravelTrailer.id) ||
        (this.addVehicleForm.get('vehicleTypeID').value == this.vehicleType.MobileHome.id);

    public setDefaultValuesForOmniSearch = (): void => {
        if (this.vehicle && !this.vehicle.vin) { // if called from omni search
            this.vehicle.vin = this.searchString;
            this.vehicleTypeId = 0;
            this.vehicle.vinTypeID = this.vinType.ManufacturerAssigned.id;
            this.vehicle.fuelTypeID = 0;
            this.vehicle.modelID = 0;
            this.vehicle.vehicleTypeID = 0;
            this.vehicle.bodyStyleTypeID = 0;
        }
    };

    public setOdometerReadingDefaults = (): void => {
        if (this.vehicle.vehicleOdometerReading.odometerReadingTypeID && this.vehicle.vehicleOdometerReading.odometerReadingTypeID > 0) {
            return;
        }

        this.vehicle.vehicleOdometerReading.odometerReadingTypeID = 0;
        this.vehicle.vehicleOdometerReading.odometerReadingUnitTypeID = 0;
    };

    public getBodyStyleTypes = (vehicleTypeId: number, bodyStyleTypeId: number): void => {
        this.vehicleAddService.getAllBodyStylesByVehicleTypeId(vehicleTypeId)
            .subscribe(result => {
                let mappedResult = new Array<IBodyStyleType>();
                let allTypes = new BodyStyleType().GetAll();

                for (let i = 0; i < result.length; i++) {
                    let foundStyle = allTypes.find(b => b.id === result[i].id);
                    mappedResult.push(foundStyle);
                }

                this.bodyStyleTypes = mappedResult;
                let bodyStyleTypeControl = this.addVehicleForm.get('bodyStyleTypeID') as UntypedFormControl;
                bodyStyleTypeControl.setErrors(null);

                if (result.length == 1) {
                    bodyStyleTypeControl.setValue(result[0]['id']);
                    this.onBodyStyleTypeSelected(result[0]['id']);
                    this.vehicle.bodyStyleTypeID = result[0]['id'];
                } else if (result.length > 1) {
                    if (bodyStyleTypeId != null) {
                        if (this.bodyStyleTypes.find(b => b.id == bodyStyleTypeId)) {
                            bodyStyleTypeControl.setValue(bodyStyleTypeId); // use the existing bodystyletypeId value
                        } else {
                            bodyStyleTypeControl.setValue(null);
                        }
                    } else {
                        bodyStyleTypeControl.setValue(null);
                    }
                } else {
                    bodyStyleTypeControl.setValue(null);
                }
            },
            e => console.log('error getting bodystyle types', e));
    };

    public getVehicleTypeBodyStyleType = (selectedVehicleTypeId: number, selectedBodyStyleTypeId: number): void => {
        if ((null != selectedVehicleTypeId) &&
            (0 != selectedVehicleTypeId) &&
            (null != selectedBodyStyleTypeId) &&
            (0 != selectedBodyStyleTypeId)
        ) {
            this.vehicleAddService.getVehicleTypeBodyStyleTypeId(selectedVehicleTypeId, selectedBodyStyleTypeId)
                .subscribe(
                    result => this.vehicleTypeBodyStyleTypeID = result,
                    e => console.log('error', e));
        }
    };

    public setModelLookupValues = (data: VehicleModel): void => {
        this.vehicleTypeId = data.vehicleTypeID;

        if ((null != data.vinTypeID) && (0 != data.vinTypeID)) {
            this.addVehicleForm.get('vin').setValue(data.vin, { emitEvent: false });
        }

        if (this.canEditVin) {
            this.addVehicleForm.get('vin').markAsTouched();
        }

        if ((null != data.vinTypeID) && (0 != data.vinTypeID)) {
            this.addVehicleForm.get('vinTypeID').setValue(data.vinTypeID);
        }

        if ((null != data.fuelTypeID) && (0 != data.fuelTypeID)) {
            this.addVehicleForm.get('fuelTypeID').setValue(data.fuelTypeID);
        }

        if ((null != data.vehicleTypeID) && (0 != data.vehicleTypeID)) {
            this.addVehicleForm.get('vehicleTypeID').setValue(data.vehicleTypeID, { emitEvent: false });
        }

        let bodyStyleTypeId: number = null;

        if ((null != data.bodyStyleTypeID) && (0 != data.bodyStyleTypeID)) {
            bodyStyleTypeId = +data.bodyStyleTypeID;
        }

        this.addVehicleForm.get('vehicleNumber').setValue(data.vehicleNumber);
        this.addVehicleForm.get('axles').setValue(data.axles);
        this.addVehicleForm.get('tonnageRatingTypeID').setValue(data.tonnageRatingTypeID);
        this.addVehicleForm.get('monthFirstRegistered').setValue(data.monthFirstRegistered);
        this.addVehicleForm.get('yearFirstRegistered').setValue(data.yearFirstRegistered);
        this.addVehicleForm.get('combinedAxles').setValue(data.combinedAxles);
        this.addVehicleForm.get('seats').setValue(data.seats);
        this.onVehicleTypeSelected(data.vehicleTypeID, bodyStyleTypeId);
        this.addVehicleForm.get('modelYear').setValue(data.modelYear);
        this.addVehicleForm.get('unladenWeight').setValue(data.unladenWeight);
        this.addVehicleForm.get('factoryListPrice').setValue(data.factoryListPrice);

        if (((null != data.primaryColorTypeID)) && (0 != data.primaryColorTypeID)) {
            this.addVehicleForm.get('primaryColorTypeID').setValue(data.primaryColorTypeID);
        }

        if ((null != data.secondaryColorTypeID) && (0 != data.secondaryColorTypeID)) {
            this.addVehicleForm.get('secondaryColorTypeID').setValue(data.secondaryColorTypeID);
        }

        this._makeAutoComplete.serviceIdPath = 'vehicle/make';
        this._makeAutoComplete.servicePath = 'vehicle/make/name';
        this._modelAutoComplete.servicePath = 'vehicle/model/make/{parentId}/name';
        this._modelAutoComplete.serviceIdPath = 'vehicle/model';

        // this.setupMakeAutoCompleteInitCompleteHandler();

        if (data.makeID != null && data.makeID != undefined) {
            this._makeAutoComplete.init(data.makeID, '');
            this._vehicleMakeAdd.init(data.makeID, '', null, null);
        }

        this.vehicleTypeBodyStyleTypeID = data.vehicleBodyStyleTypeMapID;
    };

    /*
    ** Event: Triggered by the selection of a Vehicle Type
    ** Action: Populates the BodyStyleType list for the Vehicle Type selected
    */
    public onVehicleTypeSelected = (selectedValue: number, bodyStyleTypeId?: number): void => {
        this.getBodyStyleTypes(selectedValue, bodyStyleTypeId);

        if (this.isLengthWidthRequired()) {
            this.addLengthWidthSeatsFields();
        } else {
            this.removeLengthWidthSeatsFields();
        }

        this.checkForcedModel();
    };

    /*
    ** Event: Triggered by the selection of a BodyStyleType
    ** Action: Sets the VehicleBodyStyleTypeID variable and emits the value
    */
    public onBodyStyleTypeSelected = (selectedBodyStyleTypeId): void => {
        if (null != this.addVehicleForm.get('vehicleTypeID').value) {
            this.getVehicleTypeBodyStyleType(this.addVehicleForm.get('vehicleTypeID').value, selectedBodyStyleTypeId);
        }
    };

    public markAsTouched = (): void => {
        if (this.canEditVin) {
            this.addVehicleForm.get('vin').markAsTouched();
        }

        this.addVehicleForm.get('modelYear').markAsTouched();
        this.addVehicleForm.get('vinTypeID').markAsTouched();
        this.addVehicleForm.get('fuelTypeID').markAsTouched();
        this.addVehicleForm.get('vehicleTypeID').markAsTouched();
        this.addVehicleForm.get('bodyStyleTypeID').markAsTouched();
        this.addVehicleForm.get('factoryListPrice').markAsTouched();

        if (this.addVehicleForm.get('trailerLength')) {
            this.addVehicleForm.get('trailerLength').markAsTouched();
            this.addVehicleForm.get('trailerWidth').markAsTouched();
        }
    };

    public addNewMake = (): void => {
        this._vehicleMakeAdd.init(null, '', null, '');
    };

    public addNewModel = (): void => {
        this._vehicleModelAdd.init((<VehicleModel> this.vehicle).makeID, this._makeAutoComplete.displayValue, null, '');
    };

    /**
     * Sets the object model properties for Make
     * @param makeId
     * @param makeDisplayValue
     */
    public setNewMake = (makeId: number, makeDisplayValue: string): void => {
        if (this.vehicle) {
            this.vehicle.makeID = makeId;
            this.vehicle.modelID = 0; // should be set to 0
            this.clearForcedModel();
        }
        this._makeAutoComplete.init(makeId, makeDisplayValue);

        this.checkForcedModel();
    };

    /**
     * Sets the object model properties for Model
     * @param modelId
     * @param modelDisplayValue
     */
    public setNewModel = (modelId: number, modelDisplayValue: string): void => {
        if (this.vehicle) {
            this.vehicle.modelID = modelId;
        }

        this._modelAutoComplete.init(modelId, modelDisplayValue);
    };

    /*
    ** Builds and validates the model
    */
    public getVehicleModel = (): Observable<VehicleModel> => {
        this.vehicle.vehicleBodyStyleTypeMapID = this.vehicleTypeBodyStyleTypeID;
        this.vehicle.notes = this.vehicleNotes;

        // this can be overwritten with multiple async calls.  So make sure we are passing the forcedModelId if we have one.
        if (this.forcedModelId) {
            this.vehicle.modelID = this.forcedModelId;
        }

        this.vehicle.vin = this.addVehicleForm.get('vin').value;
        this.vehicle.vinTypeID = this.addVehicleForm.get('vinTypeID').value;
        this.vehicle.vehicleNumber = this.addVehicleForm.get('vehicleNumber').value;
        this.vehicle.modelYear = this.addVehicleForm.get('modelYear').value;
        this.vehicle.fuelTypeID = this.addVehicleForm.get('fuelTypeID').value;
        this.vehicle.factoryListPrice = this.addVehicleForm.get('factoryListPrice').value;
        this.vehicle.unladenWeight = this.addVehicleForm.get('unladenWeight').value;
        this.vehicle.axles = this.addVehicleForm.get('axles').value;
        this.vehicle.vehicleTypeID = this.addVehicleForm.get('vehicleTypeID').value;
        this.vehicle.bodyStyleTypeID = this.addVehicleForm.get('bodyStyleTypeID').value;
        this.vehicle.primaryColorTypeID = this.addVehicleForm.get('primaryColorTypeID').value;
        this.vehicle.secondaryColorTypeID = this.addVehicleForm.get('secondaryColorTypeID').value;
        this.vehicle.monthFirstRegistered = this.addVehicleForm.get('monthFirstRegistered').value;
        this.vehicle.yearFirstRegistered = this.addVehicleForm.get('yearFirstRegistered').value;
        this.vehicle.combinedAxles = this.addVehicleForm.get('combinedAxles').value;
        this.vehicle.seats = this.addVehicleForm.get('seats').value;
        let tonnageRatingTypeId = this.addVehicleForm.get('tonnageRatingDisplayTypeId').value;

        if (this.isLengthWidthRequired()) {
            this.vehicle.trailerLength = this.addVehicleForm.get('trailerLength').value;
            this.vehicle.trailerWidth = this.addVehicleForm.get('trailerWidth').value;
            this.vehicle.seats = this.addVehicleForm.get('seats').value;
        }

        if (null == tonnageRatingTypeId) {
            return of(this.vehicle);
        }

        return this.vehicleService.getTonnageValue(tonnageRatingTypeId)
            .pipe(map(r => {
                this.vehicle.tonnageRatingTypeID = r;

                return this.vehicle;
            }));
    };

    /**
     * /
     * Updated brands from VehicleBrands component
     * @param data
     */
    public updateBrands = (data: any): void => {
        this.vehicle.vehicleBrands = data;
    };

    public validateInput = (): string[] => {
        let errors = new Array<string>();
        let vin = this.addVehicleForm.get('vin').value;

        if (!vin) {
            errors.push('VIN is required.');
        }

        let tempErrors = [];
        errors.forEach(error => {
            let state: ValidationStateModel = new ValidationStateModel();
            state.validationType = 'Error';
            tempErrors.push({ errorMessage: error, state: state });
        });
        this.validationModel = new ValidationModel(tempErrors);

        return errors;
    };

    public validateModel = (): Observable<string[]> => {
        let errors = new Array<string>();
        return this.getVehicleModel()
            .pipe(map(r => {
                if (+r.vehicleTypeID === this.vehicleType.Truck.id || +r.vehicleTypeID === this.vehicleType.Van.id) {
                    if (r.tonnageRatingTypeID === null || +r.tonnageRatingTypeID <= 0) {
                        errors.push('Tonnage must be provided for the selected vehicle type.');
                    }
                }

                if (r.makeID <= 0) {
                    errors.push('Make is required.');
                }

                // -1 is reserved for a forced model
                if (r.modelID == 0 || r.modelID < -1) {
                    errors.push('Model is required.');
                }

                let tempErrors = [];
                errors.forEach(error => {
                    let state: ValidationStateModel = new ValidationStateModel();
                    state.validationType = 'Error';
                    tempErrors.push({ errorMessage: error, state: state });
                });
                this.validationModel = new ValidationModel(tempErrors);

                return errors;
            }));
    };

    public isNumericKey = (event: KeyboardEvent): boolean => {
        if (event.key == null) {
            return true;
        }
        if ((' ' == event.key) || (isNaN(+event.key))) {
            event.stopPropagation();
            event.preventDefault();

            return false;
        }

        return true;
    };

    private setupMakeAutoCompleteInitCompleteHandler(): void {
        this._makeAutoComplete.initComplete.subscribe(r => {
            if (this.vehicle && this.vehicle.modelID != null && this.vehicle.modelID != undefined) {
                this._modelAutoComplete.init(this.vehicle.modelID, null);
                this._vehicleModelAdd.init(this.vehicle.makeID, '', this.vehicle.modelID, null);
            }
        }, err => console.log(err));
    }

    private getNewVehiclesFromVin(): void {
        this.vehicleService.getNewVehicles(this.addVehicleForm.get('vin').value)
            .subscribe(
                r => {
                    if (r) {
                        let vehicleResults = <VehicleModel[]> r;

                        for (var i = 0; i < vehicleResults.length; i++) {
                            let vehicleResult = vehicleResults[i];
                            if (this.activeFuelTypes.filter(f => f.id == vehicleResult.fuelTypeID).length == 0) {
                                vehicleResult.fuelTypeID = 0;
                            }
                        }

                        if (vehicleResults.length == 1) {
                            this.setVehicle(vehicleResults[0]);
                        } else if (vehicleResults.length > 1) {
                            this.vehicles = vehicleResults;
                        } else if (vehicleResults.length == 0) {
                            // if no vehicle is found then unblock the make/model input disabling.
                            this.blockUserInput = false;
                        }
                    } else {
                        // if no vehicle is found then unblock the make/model input disabling.
                        this.blockUserInput = false;
                    }
                },
                err => {
                    this.blockUserInput = false;
                    console.log(err);
                }
            );
    }

    private setTonnageRating(): void {
        let tonnageRatingTypeId = this.addVehicleForm.get('tonnageRatingTypeID').value;

        if (tonnageRatingTypeId > 0) {
            this.vehicleService.getTonnageDisplayValue(tonnageRatingTypeId)
                .subscribe(
                    r => this.addVehicleForm.get('tonnageRatingDisplayTypeId').setValue(r),
                    err => {
                        console.log(err);
                    });
        }
    }

    private setupBrands(): void {
    }

    private setupLookupValues(): void {
        setTimeout(() => {
            if (this.vehicle) {
                this.setModelLookupValues(this.vehicle);
                this.dataLoaded = true;
            }
        }, 10);
    }

    private setupMakeAutoCompleteHandler(): void {
        this._makeAutoComplete.selectedItem$.subscribe(selectedMake => {
            if (this.vehicle) {
                let origonalMakeId = this.vehicle.makeID;

                this.vehicle.makeID = +selectedMake.selectedValue;

                if (this.vehicle.makeID === 0) {
                    this.vehicle.makeID = null;
                } else {
                    if (origonalMakeId !== null && origonalMakeId != this.vehicle.makeID) {
                        this.vehicle.modelID = 0;
                        this.vehicle.modelDisplayValue = null;
                        this.clearForcedModel();
                    }
                }

                this.vehicle.makeDisplayValue = selectedMake.selectedText;
                this._modelAutoComplete.parentValue = selectedMake.selectedValue;
                this._modelAutoComplete.clear();
                this._modelAutoComplete.disabled = (selectedMake.selectedValue == null);

                if (!this.vehicle.makeID) {
                    this.blockUserInput = false;
                }

                this.checkForcedModel();
            } else {
                this.blockUserInput = false;
            }
        });
    }

    public ifCabAndChaise(): boolean {
        if (this.skipCabChasieValidation) { // for TrR Corrections and recreate
            return true;
        }
        if (!this.vehicle) {
            return false;
        }
        if (+this.addVehicleForm.get('vehicleTypeID').value == this.vehicleType.MotorHome.id) { // 101467
            return true;
        }

        let selectedBodyStyleTypeID: number = +this.vehicle.bodyStyleTypeID;
        let cabAndChasieBodyTypes: number[] = [59, 320, 321, 322];

        if (cabAndChasieBodyTypes.findIndex(s => s == selectedBodyStyleTypeID) > -1 || !this.vehicle.makeID) {
            return true;
        }

        return false;
    }

    private checkForcedModel(): void {
        if (this.vehicle.makeID > 0 && this.addVehicleForm.get('vehicleTypeID').value == 12) {
            if (this.forcedModel == null) {
                this.vehicleAddService.getModelByMakeId(this.vehicle.makeID, 'Trailer')
                    .subscribe(s => {
                        if (s.length > 0) {
                            this.vehicle.modelID = s[0].id;
                            this.forcedModelId = s[0].id;
                            this.vehicle.modelDisplayValue = s[0].modelValue;
                            this.forcedModel = s[0].modelValue;
                        } else {
                            this.vehicle.modelID = -1;
                            this.forcedModelId = -1;
                            this.vehicle.modelDisplayValue = 'Trailer';
                            this.forcedModel = 'Trailer';
                        }
                    });
            } else {
                // this.clearForcedModel();
            }
        } else if (this.vehicle.makeID > 0 && this.addVehicleForm.get('vehicleTypeID').value == this.vehicleType.MobileHome.id) {
            this.vehicleAddService.getModelByMakeId(this.vehicle.makeID, 'Mobile Home')
                .subscribe(s => {
                    if (s.length > 0) {
                        this.vehicle.modelID = s[0].id;
                        this.forcedModelId = s[0].id;
                        this.vehicle.modelDisplayValue = s[0].modelDisplayValue;
                        this.forcedModel = s[0].modelDisplayValue;
                    } else {
                        this.vehicle.modelID = -1;
                        this.forcedModelId = -1;
                        this.vehicle.modelDisplayValue = 'Mobile Home';
                        this.forcedModel = 'Mobile Home';
                    }
                });
        } else {
            if (this.forcedModel != null) {
                this.clearForcedModel();
                this._modelAutoComplete.clear();
            }
        }
    }

    private clearForcedModel(): void {
        this.forcedModel = null;
        this.forcedModelId = null;
        this.vehicle.modelID = 0;
        this.vehicle.modelDisplayValue = null;
    }

    private setupModelAutoCompleteHandler(): void {
        this._modelAutoComplete.selectedItem$
            .pipe(
                map(selectedModel => !!this.vehicle ? selectedModel : null),
                filter(selectedModel => !!selectedModel))
            .subscribe(selectedModel => {
                this.vehicle.modelID = +selectedModel.selectedValue;
                this.vehicle.modelDisplayValue = selectedModel.selectedText;
                this.blockUserInput = false;
            }, () => {
                this.blockUserInput = false;
            });
    }

    private setupTypes(): void {
        this.tonnageRatingDisplayTypes = new TonnageRatingDisplayType().GetAll();
        this.fuelTypes = new FuelType().GetAll();
        this.activeFuelTypes = new FuelType().GetAll().filter(f => f.active);
        this.odometerReadingTypes = new OdometerReadingType().GetAll();
        this.odometerReadingUnitTypes = new OdometerReadingUnitType().GetAll();
        this.vinTypes = new VINType().GetAll();
        this.tonnageRatingTypes = new TonnageRatingType().GetAll();
        this.colorTypes = new VehicleColorType().GetAll();
        this.vehicleTypes = this.vehicleType.GetActive();
        this.bodyStyleTypes = new BodyStyleType().GetAll();
        this.grossVehicleWeightRatingTypes = new GrossVehicleWeightRatingType().GetAll();
    }

    private setupVehicleModelAddHandler(): void {
        this._vehicleModelAdd.selectedMakeModel$.subscribe(makeModel => {
            this.setNewModel(makeModel.modelId, makeModel.modelDisplayValue);
        });
    }

    private setupVehicleMakeAddHandler(): void {
        this._vehicleMakeAdd.selectedMakeModel$.subscribe(makeModel => {
            this.setNewMake(makeModel.makeId, makeModel.makeDisplayValue);
            this.setNewModel(makeModel.modelId, makeModel.modelDisplayValue);
        });
    }

    private showTonnageRating(): void {
        let tonnageRatingTypeId = this.addVehicleForm.get('tonnageRatingTypeID').value;

        if (tonnageRatingTypeId) {
            this.vehicleService.getTonnageDisplayValue(tonnageRatingTypeId)
                .subscribe(
                    r => this.addVehicleForm.get('tonnageRatingDisplayTypeId').setValue(r),
                    err => console.log(err));
        }
    }

    // handles any errors that occur from services
    public handleValidationResult(error: any): void {
        this.validationModel = new ValidationModel(error);
    }

    public addLengthWidthSeatsFields(): void {
        if (!this.addVehicleForm.get('trailerLength')) {
            this.addVehicleForm.addControl('trailerLength', new UntypedFormControl(this.vehicle.trailerLength, ControlValidationMessagesService.positiveIntegerRequired));
            this.addVehicleForm.addControl('trailerWidth', new UntypedFormControl(this.vehicle.trailerWidth, ControlValidationMessagesService.positiveIntegerRequired));
        }
    }

    public removeLengthWidthSeatsFields(): void {
        if (this.addVehicleForm.get('trailerLength')) {
            this.addVehicleForm.removeControl('trailerLength');
            this.addVehicleForm.removeControl('trailerWidth');
        }
    }
}
